import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

import type { EscritorioMembro } from '@/api/client';
import type { Equipe } from '@/api/equipes';
import {
  aplicarAoAcervo,
  avaliarAcervoComIa,
  previaDoAcervo,
  type PreviaDoAcervo,
} from '@/api/regrasDistribuicao';
import { descreverResponsaveis } from '@/components/escritorio/ResponsaveisDialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useNotification } from '@/context/NotificationProvider';
import { classifyError, notifyMutationError } from '@/lib/errors';
import { queryKeys } from '@/lib/pwa/queryConfig';

interface AplicarAoAcervoDialogProps {
  escritorioId: string;
  open: boolean;
  onClose: () => void;
  membros: EscritorioMembro[];
  equipes: Equipe[];
}

const processos = (n: number) => (n === 1 ? '1 processo' : `${n.toLocaleString('pt-BR')} processos`);
const creditos = (n: number) => (n === 1 ? '1 crédito' : `${n.toLocaleString('pt-BR')} créditos`);

/**
 * As regras aplicadas, de uma vez e para trás, ao acervo que ninguém cuida
 * (docs/PLANO_REGRAS_DISTRIBUICAO.md, Bloco 3): o que cada regra leva, na ordem, e a confirmação.
 * O servidor faz em segundo plano.
 */
export function AplicarAoAcervoDialog({
  escritorioId,
  open,
  onClose,
  membros,
  equipes,
}: AplicarAoAcervoDialogProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();

  const previa = useQuery({
    queryKey: ['regras-distribuicao', 'acervo-previa', escritorioId],
    queryFn: () => previaDoAcervo(escritorioId),
    enabled: open,
    // Sempre a conta do momento: as regras e o acervo mudam entre uma abertura e outra (e a
    // versão da prévia vai no pedido: uma velha daria 409).
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    retry: false,
    // Enquanto a IA avalia o acervo (Batch: minutos a horas), os números se refazem sozinhos.
    refetchInterval: (q) => (q.state.data?.ia_avaliando ? 30_000 : false),
  });

  const avaliar = useMutation({
    mutationFn: () => avaliarAcervoComIa(escritorioId),
    onSuccess: () => {
      showSuccess('A IA está avaliando o acervo; os números se atualizam sozinhos');
      // Na hora: o "Distribuir" não fica liberado com a prévia de antes do pedido.
      queryClient.setQueryData<PreviaDoAcervo>(['regras-distribuicao', 'acervo-previa', escritorioId], (p) =>
        p ? { ...p, ia_avaliando: true, ia_falha: null } : p,
      );
      previa.refetch();
    },
    onError: (err) => {
      notifyMutationError(err, showError);
      previa.refetch();
    },
  });

  const aplicar = useMutation({
    mutationFn: (versao: string) => aplicarAoAcervo(escritorioId, versao),
    onSuccess: async (lote) => {
      showSuccess('Aplicando as regras ao acervo em segundo plano');
      // Uma leitura do andamento já a caminho (de antes do pedido) não pode apagar o "aplicando".
      await queryClient.cancelQueries({ queryKey: queryKeys.escritorios.acervo(escritorioId) });
      queryClient.setQueryData(queryKeys.escritorios.acervo(escritorioId), lote);
      onClose();
    },
    onError: (err) => {
      notifyMutationError(err, showError);
      // Outra tela já pediu, ou as regras mudaram desde a prévia: a lista e o andamento voltam a
      // valer do servidor e o diálogo fecha (reaberto, a prévia é refeita).
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.regras(escritorioId) });
      if (isAxiosError(err) && err.response?.status === 409) onClose();
    },
  });

  const dados = previa.data;
  const vao = dados?.vao ?? 0;
  // A posição de cada regra na ordem (a prévia vem com todas as que contam, na ordem).
  const linhas = (dados?.regras ?? [])
    .map((r, i) => ({ ...r, posicao: i + 1 }))
    .filter((r) => r.vao > 0 || r.aguardando > 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Aplicar as regras ao acervo existente</DialogTitle>
          <DialogDescription>
            Vale para trás: distribui agora os processos que já estão no escritório sem responsável. O que alguém
            já distribuiu não muda.
          </DialogDescription>
        </DialogHeader>

        {previa.isLoading ? (
          <div className="flex justify-center py-6">
            <Spinner size={32} label="Contando os processos" />
          </div>
        ) : previa.error ? (
          <Alert variant="destructive">
            <AlertDescription>{classifyError(previa.error).message}</AlertDescription>
          </Alert>
        ) : (
          dados && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                {dados.parcial
                  ? `Dos ${dados.olhados.toLocaleString('pt-BR')} primeiros sem responsável (há mais; a aplicação vai pelo acervo todo), pela ordem das regras:`
                  : `Dos ${processos(dados.olhados)} sem responsável, pela ordem das regras:`}
              </p>

              {linhas.length > 0 && (
                <ul className="divide-y rounded-md border" aria-label="O que cada regra leva">
                  {linhas.map((r) => {
                    const destino = descreverResponsaveis([r.destino], equipes, membros);
                    // O nome sugerido já diz o destino ("INSS → Fulano"): não repetir.
                    const nomeDizODestino = r.nome.toLocaleLowerCase('pt-BR').includes(destino.toLocaleLowerCase('pt-BR'));
                    return (
                      <li key={r.regra_id} className="flex items-start gap-3 px-3 py-2">
                        <span className="w-4 shrink-0 pt-px text-xs tabular-nums text-muted-foreground">
                          {r.posicao}.
                        </span>
                        <div className="min-w-0 flex-1 space-y-0.5">
                          <p className="font-medium leading-snug [overflow-wrap:anywhere]">{r.nome}</p>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                            {r.destino_invalido ? (
                              <span className="text-destructive">
                                O destino não vale mais: {processos(r.vao + r.aguardando)} param aqui, sem responsável
                              </span>
                            ) : (
                              !nomeDizODestino && <span className="[overflow-wrap:anywhere]">para {destino}</span>
                            )}
                            {!r.destino_invalido && r.aguardando > 0 && (
                              <span>+{processos(r.aguardando)} aguardam dados</span>
                            )}
                          </div>
                        </div>
                        <span className="shrink-0 font-semibold tabular-nums">
                          {r.destino_invalido ? '—' : r.vao.toLocaleString('pt-BR')}
                        </span>
                      </li>
                    );
                  })}
                  <li className="flex items-center justify-between gap-3 bg-muted/50 px-3 py-2 font-medium">
                    <span>Total a distribuir</span>
                    <span className="tabular-nums">{vao.toLocaleString('pt-BR')}</span>
                  </li>
                </ul>
              )}

              {dados.sem_regra > 0 && (
                <p className="text-xs text-muted-foreground">
                  {processos(dados.sem_regra)} sem regra que case continuam sem responsável.
                </p>
              )}

              {dados.ia_avaliando ? (
                <div role="status" className="flex items-center gap-2 rounded-md bg-muted/60 p-2.5 text-xs">
                  <Spinner size={16} />
                  <span>
                    Avaliando o acervo com a IA… Os números acima se atualizam sozinhos; pode levar de minutos a
                    algumas horas.
                  </span>
                </div>
              ) : dados.com_ia && (dados.ia_processos ?? 0) === 0 ? (
                <p className="text-xs text-muted-foreground">
                  A IA já avaliou o acervo: as regras com critério da IA entram na conta acima.
                </p>
              ) : (
                (dados.ia_processos ?? 0) > 0 && (
                  <div className="space-y-2 rounded-md border p-2.5 text-xs">
                    {dados.ia_falha && (
                      <p className="text-destructive">A última avaliação não terminou: {dados.ia_falha}.</p>
                    )}
                    <p>
                      A IA ainda não avaliou {processos(dados.ia_processos ?? 0)} do acervo
                      {dados.parcial && ' (entre os 20 mil primeiros; o resto entra na mesma avaliação)'}. Sem avaliar,
                      as regras com critério da IA não levam esses processos.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-11 sm:h-8"
                      disabled={avaliar.isPending}
                      onClick={() => avaliar.mutate()}
                    >
                      {avaliar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
                      Avaliar com a IA ({creditos(dados.ia_creditos ?? 0)})
                    </Button>
                  </div>
                )
              )}
              <p className="text-xs text-muted-foreground">
                Roda em segundo plano. Quem recebe é avisado por e-mail no fim, e a aplicação inteira pode ser
                desfeita depois.
              </p>
            </div>
          )
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" className="h-11 sm:h-9" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="button"
            className="h-11 sm:h-9"
            disabled={!vao || aplicar.isPending || !!dados?.ia_avaliando}
            onClick={() => dados && aplicar.mutate(dados.versao)}
          >
            {aplicar.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
            {!dados
              ? 'Distribuir'
              : !vao
                ? 'Nada a distribuir'
                : dados.parcial
                  ? `Distribuir (${processos(vao)} nos primeiros)`
                  : `Distribuir ${processos(vao)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
